/**
 * What changed since the snapshot — the digest, then the list.
 *
 * `diffSnapshot` has always returned a complete `entries[]`: every issue, its
 * kind, both date pairs and a signed delta. The dialog used to read four counts
 * off it and flatten them into a single sentence, so a reader could learn that
 * "1 moved" but never which one — and an issue added or gone since the snapshot
 * was counted and then never located anywhere.
 *
 * The digest is two pieces the dialog places separately: the colour-coded
 * counts (which double as filters) in the header's corner, and the completion
 * shift stated as a line under the title.
 */
import { CheckCircle2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { fill } from "@/lib/i18n/fill";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { formatDateOnly } from "@/domains/pm/utils/protocol";
import {
  isUnchanged,
  type SnapshotChangeKind,
  type SnapshotDiff,
  type SnapshotDiffEntry,
} from "@/domains/pm/utils/scheduleSnapshotDiff";
import { CHANGE_KINDS, CHANGE_TONE } from "./snapshotDiffTones";

/**
 * The four counts on one line, in the header's top middle. Each is also a
 * filter toggle; an active one is marked by the brand-red border, the same
 * red that marks a selected row on the chart.
 */
export function SnapshotChangeCounts({
  diff,
  active,
  onToggleKind,
}: {
  diff: SnapshotDiff;
  /** Kinds the reader has narrowed to. Empty means "no filter". */
  active: Set<SnapshotChangeKind>;
  onToggleKind: (kind: SnapshotChangeKind) => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;
  const label: Record<SnapshotChangeKind, string> = {
    moved: copy.compareMoved,
    added: copy.compareAdded,
    removed: copy.compareGone,
    unchanged: copy.compareUnchanged,
  };
  const count: Record<SnapshotChangeKind, number> = {
    moved: diff.moved,
    added: diff.added,
    removed: diff.removed,
    unchanged: diff.unchanged,
  };

  return (
    <div className="flex shrink-0 flex-wrap items-center justify-center gap-1">
      {CHANGE_KINDS.map((kind) => {
        const tone = CHANGE_TONE[kind];
        const on = active.has(kind);
        return (
          <button
            key={kind}
            type="button"
            onClick={() => onToggleKind(kind)}
            aria-pressed={on}
            disabled={count[kind] === 0}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors",
              on ? "border-[#ef4129]" : "border-transparent hover:border-[#ef4129]/40",
              count[kind] === 0 && "opacity-40"
            )}
          >
            <span className={cn("size-2 shrink-0 rounded-full", tone.bar)} />
            <span className="text-muted-foreground">{label[kind]}</span>
            <span className={cn("tabular-nums", tone.text)}>
              {tone.sign}
              {count[kind]}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * Completion, stated rather than boxed: it is simply a fact about the
 * schedule, not an alert. Renders nothing when completion did not move.
 */
function completionDates(
  completion: SnapshotDiff["completion"],
  language: Parameters<typeof formatDateOnly>[1]
): string {
  return `${formatDateOnly(completion.snapshot ?? "", language)} → ${formatDateOnly(completion.current ?? "", language)}`;
}

export function SnapshotCompletionShift({ diff }: { diff: SnapshotDiff }) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.gantt;
  const completionDelta = diff.completion.deltaDays;
  if (completionDelta == null || completionDelta === 0) return null;

  return (
    <p className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground">
      <span className="font-medium text-foreground">{copy.compareCompletion}</span>
      <span className="tabular-nums">{completionDates(diff.completion, language)}</span>
      <span
        className={cn(
          "font-medium tabular-nums",
          completionDelta > 0 ? "text-amber-600" : "text-emerald-600"
        )}
      >
        {completionDelta > 0
          ? fill(copy.compareDaysLater, { count: String(completionDelta) })
          : fill(copy.compareDaysEarlier, { count: String(Math.abs(completionDelta)) })}
      </span>
    </p>
  );
}

export function SnapshotChangeList({
  diff,
  rows,
  selectedIssueId,
  onSelectIssue,
}: {
  diff: SnapshotDiff;
  /** Already filtered and ordered by the dialog. */
  rows: SnapshotDiffEntry[];
  selectedIssueId?: number;
  onSelectIssue?: (issueId: number) => void;
}) {
  const { t } = useLanguage();
  const copy = t.pm.issues.gantt;

  if (isUnchanged(diff)) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-2 text-center">
        <CheckCircle2 className="size-8 text-emerald-500" />
        <p className="text-sm text-muted-foreground">{copy.compareNothingChanged}</p>
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <p className="px-1 py-6 text-center text-xs text-muted-foreground">{copy.compareNoRows}</p>
    );
  }

  // Grouped, but the ORDER within is the caller's — so this and the chart on
  // the other tab run in step.
  const groups = CHANGE_KINDS.map((kind) => ({
    kind,
    items: rows.filter((r) => r.kind === kind),
  })).filter((g) => g.items.length > 0);

  const kindLabel: Record<SnapshotChangeKind, string> = {
    moved: copy.compareMoved,
    added: copy.compareAdded,
    removed: copy.compareGone,
    unchanged: copy.compareUnchanged,
  };

  return (
    // The issues list's own table, deliberately: this answers the same question
    // about the same rows, so it should not invent a second way to show them.
    // No fill on a row — hover and selection are the only tints.
    <table className="w-full border-collapse">
      <thead>
        <tr className="border-b border-border/60">
          <Th className="min-w-[220px]">{copy.compareColIssue}</Th>
          <Th>{copy.compareColWas}</Th>
          <Th>{copy.compareColNow}</Th>
          <Th className="text-right">{copy.compareColChange}</Th>
        </tr>
      </thead>
      {groups.map((group) => (
        <tbody key={group.kind}>
          <tr>
            <td colSpan={4} className="px-1 py-2.5">
              <span className="flex items-center gap-1.5">
                <span className={cn("size-2 rounded-full", CHANGE_TONE[group.kind].bar)} />
                <span className="text-sm font-medium">{kindLabel[group.kind]}</span>
                <span className="inline-flex h-[18px] min-w-[20px] items-center justify-center rounded-full bg-primary/10 px-1.5 text-[10px] font-semibold text-primary">
                  {group.items.length}
                </span>
              </span>
            </td>
          </tr>
          {group.items.map((row) => (
            <ChangeRow
              key={row.issue_id}
              row={row}
              selected={selectedIssueId === row.issue_id}
              onSelect={onSelectIssue}
            />
          ))}
        </tbody>
      ))}
    </table>
  );
}

function Th({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <th
      className={cn(
        "px-3 py-1.5 text-left text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
        className
      )}
    >
      {children}
    </th>
  );
}

function ChangeRow({
  row,
  selected,
  onSelect,
}: {
  row: SnapshotDiffEntry;
  selected: boolean;
  onSelect?: (issueId: number) => void;
}) {
  const { t, language } = useLanguage();
  const copy = t.pm.issues.gantt;

  const span = (start: string | null, due: string | null) => {
    if (!start && !due) return "—";
    const a = start ? formatDateOnly(start, language) : "—";
    const b = due ? formatDateOnly(due, language) : "—";
    return start && due ? `${a} – ${b}` : a === "—" ? b : a;
  };

  /** What actually happened, in words. A change at the start alone has no
   *  due-date delta, so saying only "moved" would leave the row explaining
   *  nothing. */
  const verdict = () => {
    if (row.deltaDays != null && row.deltaDays !== 0) {
      return row.deltaDays > 0
        ? fill(copy.compareDaysLater, { count: String(row.deltaDays) })
        : fill(copy.compareDaysEarlier, { count: String(Math.abs(row.deltaDays)) });
    }
    if (row.durationDeltaDays != null && row.durationDeltaDays !== 0) {
      return row.durationDeltaDays > 0
        ? fill(copy.compareDaysLonger, { count: String(row.durationDeltaDays) })
        : fill(copy.compareDaysShorter, { count: String(Math.abs(row.durationDeltaDays)) });
    }
    return null;
  };

  const later = (row.deltaDays ?? 0) > 0 || (row.durationDeltaDays ?? 0) > 0;

  return (
    <tr
      onClick={() => onSelect?.(row.issue_id)}
      className={cn(
        "cursor-pointer border-b transition-colors",
        selected ? "bg-primary/5" : "hover:bg-[#f7f7f7]"
      )}
    >
      <td className="px-3 py-2.5">
        <span className="block text-[11px] text-muted-foreground">#{row.issue_number}</span>
        <span
          className={cn(
            "block text-sm leading-snug",
            row.kind === "removed" && "text-muted-foreground"
          )}
        >
          {row.title}
        </span>
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-[11px] tabular-nums text-muted-foreground">
        <span className="sr-only">{copy.compareLegendBaseline}: </span>
        {row.kind === "added" ? "—" : span(row.snapshotStart, row.snapshotDue)}
      </td>
      <td className="whitespace-nowrap px-3 py-2.5 text-[11px] tabular-nums text-muted-foreground">
        <span className="sr-only">{copy.compareLegendToday}: </span>
        {row.kind === "removed" ? "—" : span(row.currentStart, row.currentDue)}
      </td>
      <td
        className={cn(
          "whitespace-nowrap px-3 py-2.5 text-right text-[11px] font-medium tabular-nums",
          verdict() ? (later ? "text-amber-600" : "text-emerald-600") : "text-muted-foreground"
        )}
      >
        {verdict() ?? "—"}
      </td>
    </tr>
  );
}
